import { useState, useCallback } from "react";
import { FileText, Download, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useGenerateMspPortfolioReport,
  useComplianceReport,
} from "@/hooks/use-api";
import { apiGet } from "@/lib/api";
import type { ComplianceReportResponse } from "@/types/api";

function periodForDays(days: number): {
  period_start: string;
  period_end: string;
} {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return {
    period_start: start.toISOString().slice(0, 10),
    period_end: end.toISOString().slice(0, 10),
  };
}

/**
 * On-demand MSP portfolio report (#783). Triggers async generation, polls the
 * returned report id, and downloads via the shared compliance GET. The report
 * also appears in the Reports-page history (it is a compliance_reports row).
 */
export function MspPortfolioReport() {
  const [days, setDays] = useState(30);
  const [reportId, setReportId] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = useGenerateMspPortfolioReport();
  const { data: reportData } = useComplianceReport(reportId);
  const report = reportData?.report ?? null;
  const inFlight =
    report?.status === "pending" || report?.status === "generating";

  const handleGenerate = useCallback(() => {
    setError(null);
    generate.mutate(periodForDays(days), {
      onSuccess: (resp) => setReportId(resp.report.id),
      onError: (err) =>
        setError(err.message || "Failed to start report generation"),
    });
  }, [days, generate]);

  const handleDownload = useCallback(async () => {
    if (!reportId) return;
    setDownloading(true);
    setError(null);
    try {
      const resp = await apiGet<ComplianceReportResponse>(
        `/api/v1/reports/compliance/${reportId}?download=true`,
      );
      if (resp.download_url) {
        window.open(resp.download_url, "_blank");
      }
    } catch {
      setError("Failed to download report. Please try again.");
    } finally {
      setDownloading(false);
    }
  }, [reportId]);

  const busy = generate.isPending || inFlight;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Portfolio Report</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Generate a PDF summarizing security posture across all client tenants.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label="Report period"
            disabled={busy}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button onClick={handleGenerate} disabled={busy}>
            {busy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileText className="mr-2 h-4 w-4" />
            )}
            Generate portfolio report
          </Button>
          {report?.status === "completed" && (
            <Button
              variant="outline"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Download PDF
            </Button>
          )}
        </div>

        {inFlight && (
          <p className="text-sm text-muted-foreground" role="status">
            Generating report…
          </p>
        )}
        {report?.status === "failed" && (
          <div
            className="flex items-center gap-2 text-sm text-red-400"
            role="alert"
          >
            <AlertCircle className="h-4 w-4" />
            Report generation failed. Please try again.
          </div>
        )}
        {error && (
          <div className="text-sm text-red-400" role="alert">
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
